import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import CustomButton from "../Common/CustomButton";
import { TagsInputUniqueKeys } from "../Common/TagInputsUniqueKeys";
import useFetch from "../Common/useFetch";
import { userContext } from "../Contexts/userContext";
import { AllowedFilterKeysVendorSide, CN_Status_Type } from "./VendorSideDashboard";

export default function VendorSideFilter({
  open,
  onClose,
  setAppliedFilters,
  appliedFilters,
}: {
  open: boolean;
  onClose: () => void;
  setAppliedFilters: React.Dispatch<React.SetStateAction<Record<AllowedFilterKeysVendorSide, string[]>>>;
  appliedFilters: Record<AllowedFilterKeysVendorSide, string[]>;
}) {
  const { actor } = useContext(userContext);
  const [grnNumbers, setGrnNumbers] = useState<string[]>(appliedFilters.grnNumber || []);
  const [invoiceNumbers, setInvoiceNumbers] = useState<string[]>(appliedFilters.invoiceNumber || []);
  const [vendorCodes, setVendorCodes] = useState<string[]>(appliedFilters.vendorCode || []);
  const [warehouseNames, setWarehouseNames] = useState<string[]>(appliedFilters.warehouseName || []);
  const [cnStatus, setCnStatus] = useState<string[]>(appliedFilters.cnStatus || []);
  const [fromEarlyPayDate, setFromEarlyPayDate] = useState<string>(
    appliedFilters.earlyPayDate ? appliedFilters.earlyPayDate[0] : null
  );
  const [toEarlyPayDate, setToEarlyPayDate] = useState<string>(
    appliedFilters.earlyPayDate ? appliedFilters.earlyPayDate[1] : null
  );

  const [grnNumberOptions, setGrnNumberOptions] = useState<{ grnNumber: string }[]>([]);
  const [invoiceNumberOptions, setInvoiceNumberOptions] = useState<{ invoiceNumber: string }[]>([]);
  const [vendorCodeOptions, setVendorCodeOptions] = useState<{ vendorCode: string }[]>([]);
  const [warehouseNameOptions, setWarehouseNameOptions] = useState<{ warehouseName: string }[]>([]);

  const getFilterOptions = async () => {
    await useFetch(`/invoices/list/2/${actor.actorId}/filters/options`, "GET", {
      failureMessage: "Failed to fetch filter options",
      thenCallBack: (response) => {
        const data = response.data.result;
        setGrnNumberOptions(data.grnNumber.map((grn) => ({ grnNumber: grn })) || []);
        setInvoiceNumberOptions(data.invoiceNumber.map((inv) => ({ invoiceNumber: inv })) || []);
        setVendorCodeOptions(data.vendorCode.map((vc) => ({ vendorCode: vc })) || []);
        setWarehouseNameOptions(data.warehouseName.map((wn) => ({ warehouseName: wn })) || []);
      },
      errorCallback: (error) => {
        console.error("Error fetching filter options:", error);
      },
    });
  };

  useEffect(() => {
    getFilterOptions();
  }, []);

  const handleApplyFilter = () => {
    setAppliedFilters({
      grnNumber: grnNumbers.filter((v) => v.trim() !== ""),
      invoiceNumber: invoiceNumbers.filter((v) => v.trim() !== ""),
      vendorCode: vendorCodes.filter((v) => v.trim() !== ""),
      warehouseName: warehouseNames.filter((v) => v.trim() !== ""),
      earlyPayDate: [fromEarlyPayDate, toEarlyPayDate],
      cnStatus: cnStatus.filter((v) => v.trim() !== ""),
    });
    onClose();
  };

  const handleClearFilters = () => {
    setAppliedFilters({
      grnNumber: [],
      invoiceNumber: [],
      vendorCode: [],
      warehouseName: [],
      earlyPayDate: [],
      cnStatus: [],
    });
    onClose();
  };

  return (
    <Dialog open={open} sx={{ "& .MuiDialog-paper": { borderRadius: "4px", minWidth: "600px" } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Apply Filter
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">GRN Number</Typography>
            <Box sx={{ width: "380px" }}>
              <TagsInputUniqueKeys
                tags={grnNumbers}
                setTags={setGrnNumbers}
                fullWidth={true}
                placeholder="Newline separated GRN Numbers"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={grnNumberOptions}
                accessor={"grnNumber"}
                uniqueKeyAccessor={"grnNumber"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Invoice Number</Typography>
            <Box sx={{ width: "380px" }}>
              <TagsInputUniqueKeys
                tags={invoiceNumbers}
                setTags={setInvoiceNumbers}
                fullWidth={true}
                placeholder="Newline separated Invoice Numbers"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={invoiceNumberOptions}
                accessor={"invoiceNumber"}
                uniqueKeyAccessor={"invoiceNumber"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Code</Typography>
            <Box sx={{ width: "380px" }}>
              <TagsInputUniqueKeys
                tags={vendorCodes}
                setTags={setVendorCodes}
                fullWidth={true}
                placeholder="Newline separated Vendor Codes"
                splitter="newline"
                limitTags={1}
                options={vendorCodeOptions}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                accessor={"vendorCode"}
                uniqueKeyAccessor={"vendorCode"}
                renderOption={(props, option) => (
                  <li {...props} key={option.vendorCode}>
                    {option.vendorCode.charAt(0).toUpperCase() + option.vendorCode.slice(1)}
                  </li>
                )}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Warehouse Names </Typography>
            <Box sx={{ width: "380px" }}>
              <TagsInputUniqueKeys
                tags={warehouseNames}
                setTags={setWarehouseNames}
                fullWidth={true}
                placeholder="Newline separated Warehouse Names"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={warehouseNameOptions}
                accessor={"warehouseName"}
                uniqueKeyAccessor={"warehouseName"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">CN Status </Typography>
            <Box sx={{ width: "380px" }}>
              <TagsInputUniqueKeys
                tags={cnStatus}
                setTags={setCnStatus}
                fullWidth={true}
                placeholder="Newline separated CN Status"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={[
                  { cnStatus: CN_Status_Type.CN_PENDING },
                  { cnStatus: CN_Status_Type.CN_VERIFIED },
                  { cnStatus: CN_Status_Type.CN_REJECTED },
                  { cnStatus: CN_Status_Type.REVIEW_PENDING },
                ]}
                accessor={"cnStatus"}
                uniqueKeyAccessor={"cnStatus"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Early Pay Date</Typography>
            <FormControl sx={{ width: "380px", ml: "auto", flexFlow: "row", justifyContent: "space-between" }}>
              <TextField
                label="From"
                InputLabelProps={{ shrink: true }}
                id="outlined-size-small"
                size="small"
                type="date"
                value={fromEarlyPayDate || ""}
                onChange={(e) => {
                  setFromEarlyPayDate(e.target.value);
                }}
              />
              <TextField
                label="To"
                InputLabelProps={{ shrink: true }}
                id="outlined-size-small"
                size="small"
                type="date"
                value={toEarlyPayDate || ""}
                onChange={(e) => {
                  setToEarlyPayDate(e.target.value);
                }}
              />
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <CustomButton
          onClick={() => {
            handleClearFilters();
          }}
          variant="text"
        >
          Clear Filter
        </CustomButton>
        <CustomButton
          variant="contained"
          onClick={() => {
            handleApplyFilter();
          }}
        >
          Apply
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
}
